import dayjs from 'dayjs'

import { asNr } from './format'

export const validateMinValue = (value: unknown, minValue: number) => {
  try {
    const number = asNr(value)
    return !Number.isNaN(number) && number >= minValue
  } catch {
    return false
  }
}

export const validateMaxValue = (value: unknown, maxValue: number) => {
  try {
    const number = asNr(value)
    return !Number.isNaN(number) && number <= maxValue
  } catch {
    return false
  }
}

// Strict: accepts only a plain base-10 integer (no leading zeros). Surrounding whitespace
// is allowed, but nothing else is coerced away — "1.5" and "090" are invalid rather than
// cleaned up (unlike validateInteger below, which strips non-digits before parsing).
export const validateStrictInteger = (value: unknown) => /^-?(0|[1-9]\d*)$/.test(String(value).trim())

export const validateInteger = (value: unknown) => {
  if (typeof value === 'string') {
    const parsedValue = parseInt(value.replace(/\D/g, ''))

    // Invalidates a string with only multiple zeroes, e.g. "000"
    if (parsedValue === 0 && value.length > 1) {
      return false
    }

    // Invalidates a string that starts with zero but has a value greater than zero, e.g. "090"
    if (parsedValue !== 0 && value[0] === '0') {
      return false
    }

    return Number.isInteger(parsedValue)
  }

  if (typeof value === 'number') {
    return Number.isInteger(value)
  }

  return false
}

export const validateEmail = (value: string) => {
  return (
    value.match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    ) !== null
  )
}

export const validatePhone = (value: string) => {
  const phoneNumber = value.replace(/ /g, '')
  return (
    (phoneNumber.match(/^(([+]46)\s*(7)|07)[0236]\s*(\d{4})\s*(\d{3})$/) ||
      phoneNumber.match(/^(([+]46)|0)\d{0,3}\d{5,8}$/)) !== null
  )
}

// Same shape as bolane-api's z.uuid(): an RFC 9562 version (1-8) and variant (8, 9, a, b).
export const validateUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)

export const validateAgeOfUser = (input: string) => {
  let personalNumber = input
  personalNumber = personalNumber.replace('-', '')
  const personalNumberMin = parseInt(dayjs().subtract(118, 'year').format('YYYYMMDD0000'), 10)
  const personalNumberMax = parseInt(dayjs().subtract(18, 'year').format('YYYYMMDD9999'), 10)

  if (parseInt(personalNumber, 10) < personalNumberMin || parseInt(personalNumber, 10) > personalNumberMax) return false
  return true
}

export const validatePersonalNumber = (input: string) => {
  let personalNumber = input

  if (!personalNumber) {
    return false
  }
  if (personalNumber.indexOf('-') === -1) {
    if (personalNumber.length !== 12) {
      return false
    }
  } else {
    personalNumber = personalNumber.replace('-', '')
  }

  // Declare variables
  const match = personalNumber.match(/^(\d{4})(\d{2})(\d{2})(\d{4})$/)
  if (!match || personalNumber.length !== 12) {
    return false
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  personalNumber = personalNumber.substring(2)

  const d = new Date(year, month - 1, day)
  let sum = 0
  const numdigits = personalNumber.length
  const parity = numdigits % 2

  let i
  let digit
  // Check valid date
  if (Object.prototype.toString.call(d) !== '[object Date]' || Number.isNaN(d.getTime())) return false
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return false
  // Check luhn algorithm
  for (i = 0; i < numdigits; i += 1) {
    digit = parseInt(personalNumber.charAt(i), 10)
    if (i % 2 === parity) {
      digit *= 2
    }
    if (digit > 9) {
      digit -= 9
    }
    sum += digit
  }
  return sum % 10 === 0
}
